// composables/useAuthToken.ts
import { ref, computed } from "vue";

// Optional: other tabs
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e: StorageEvent) => {
    if (e.storageArea !== localStorage) return;
    if (e.key === TOKEN_KEY || e.key === EXPIRATION_KEY) syncFromStorage();
  });
}

const TOKEN_KEY = "token";
const EXPIRATION_KEY = "expiration";

const rawToken = ref<string | null>(safeGet(TOKEN_KEY));
const rawExpirationIso = ref<string | null>(safeGet(EXPIRATION_KEY));

const syncFromStorage = () => {
  rawToken.value = safeGet(TOKEN_KEY);
  rawExpirationIso.value = safeGet(EXPIRATION_KEY);
};

// ✅ register once
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e: StorageEvent) => {
    if (e.storageArea !== localStorage) return;
    if (e.key === TOKEN_KEY || e.key === EXPIRATION_KEY) syncFromStorage();
  });
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

function safeRemove(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function parseExpirationMs(iso: string | null): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

export function useAuthToken() {
  // const rawToken = ref<string | null>(safeGet(TOKEN_KEY));
  // const rawExpirationIso = ref<string | null>(safeGet(EXPIRATION_KEY));

  const expirationMs = computed(() => parseExpirationMs(rawExpirationIso.value));

  const tokenDurationMs = computed(() => {
    const exp = expirationMs.value;
    if (exp == null) return 0;
    return Math.max(0, exp - Date.now());
  });

  const isExpired = computed(() => tokenDurationMs.value <= 0);

  const token = computed<string | null>(() => {
    if (!rawToken.value) return null;
    if (isExpired.value) return null;
    return rawToken.value;
  });

  const hasValidToken = computed(() => {
    const t = token.value;
    return typeof t === "string" && t.trim().length > 0;
  });

  const setToken = (value: string, expiresAt: Date) => {
    rawToken.value = value;
    rawExpirationIso.value = expiresAt.toISOString();

    safeSet(TOKEN_KEY, value);
    safeSet(EXPIRATION_KEY, rawExpirationIso.value);
  };

  const clearToken = () => {
    rawToken.value = null;
    rawExpirationIso.value = null;

    safeRemove(TOKEN_KEY);
    safeRemove(EXPIRATION_KEY);
  };

  const clearIfExpired = () => {
    if (isExpired.value) clearToken();
  };

  // Optional: keep multiple tabs in sync
  // const syncFromStorage = () => {
  //   rawToken.value = safeGet(TOKEN_KEY);
  //   rawExpirationIso.value = safeGet(EXPIRATION_KEY);
  // };

  // const onStorage = (e: StorageEvent) => {
  //   if (e.storageArea !== localStorage) return;
  //   if (e.key === TOKEN_KEY || e.key === EXPIRATION_KEY) syncFromStorage();
  // };

  // onMounted(() => window.addEventListener("storage", onStorage));
  // onBeforeUnmount(() => window.removeEventListener("storage", onStorage));

  return {
    /** state */
    token,
    hasValidToken,
    isExpired,
    expirationMs,

    /** actions */
    setToken,
    clearToken,
    clearIfExpired,
    syncFromStorage,

    /** derived */
    getTokenDuration: () => tokenDurationMs.value,
  };
}
